import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { apiFetch } from "../lib/api";

const Badge = ({ status }) => {
  const map = {
    pass: { cls: "bg-emerald/10 text-emerald border-emerald/30", Icon: CheckCircle2, label: "PASS" },
    warn: { cls: "bg-amber/10 text-amber border-amber/40", Icon: AlertTriangle, label: "WARNING" },
    fail: { cls: "bg-burgundy/10 text-burgundy border-burgundy/40", Icon: XCircle, label: "FAIL" },
  };
  const { cls, Icon, label } = map[status] || map.warn;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${cls}`} data-testid={`badge-${status}`}>
      <Icon className="h-3 w-3" strokeWidth={2} /> {label}
    </span>
  );
};

export default function AiDiscovery() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    apiFetch(`/admin/ai-discovery?origin=${encodeURIComponent(window.location.origin)}`)
      .then(setReport)
      .catch((e) => setError(e.message || "Audit failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div data-testid="ai-discovery-page">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Technical</p>
          <h1 className="mt-2 font-serif text-3xl text-ink">AI Discovery Readiness</h1>
          <p className="mt-2 max-w-xl text-sm text-ink/60">
            Live audit of crawler access, sitemap, structured data and entity signals for the public site — especially the Klinik Pergigian Irma Ariandi dental hub.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="ai-discovery-refresh" className="flex items-center gap-2 border border-ink/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:border-amber disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Re-run audit
        </button>
      </div>

      {error && <p className="mt-6 border border-burgundy/30 bg-burgundy/5 px-4 py-3 text-sm text-burgundy" data-testid="ai-discovery-error">{error}</p>}

      {report && (
        <>
          <div className="mt-8 grid gap-4 sm:grid-cols-4" data-testid="ai-discovery-summary">
            <div className="border border-ink/10 bg-warmwhite p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Audited origin</p>
              <p className="mt-2 break-all text-sm font-semibold text-ink">{report.origin}</p>
            </div>
            <div className="border border-ink/10 bg-warmwhite p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Pass</p>
              <p className="mt-2 font-serif text-3xl text-emerald" data-testid="summary-pass">{report.summary.pass}</p>
            </div>
            <div className="border border-ink/10 bg-warmwhite p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Warnings</p>
              <p className="mt-2 font-serif text-3xl text-amber" data-testid="summary-warn">{report.summary.warn}</p>
            </div>
            <div className="border border-ink/10 bg-warmwhite p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Failures</p>
              <p className="mt-2 font-serif text-3xl text-burgundy" data-testid="summary-fail">{report.summary.fail}</p>
            </div>
          </div>

          {report.sitemap?.total_urls != null && (
            <div className="mt-6 border border-ink/10 bg-warmwhite p-6" data-testid="sitemap-stats">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Sitemap</p>
              <div className="mt-3 grid gap-4 text-sm sm:grid-cols-4">
                <p><span className="font-semibold text-ink">{report.sitemap.total_urls}</span> <span className="text-ink/60">total URLs</span></p>
                <p><span className="font-semibold text-ink">{report.sitemap.dental_urls}</span> <span className="text-ink/60">dental URLs</span></p>
                <p><span className="text-ink/60">Newest content:</span> <span className="font-semibold text-ink">{report.sitemap.newest_lastmod || "—"}</span></p>
                <p className="break-all"><span className="text-ink/60">Generated:</span> <span className="font-semibold text-ink">{new Date(report.sitemap.generated_at).toLocaleString()}</span></p>
              </div>
            </div>
          )}

          <div className="mt-6 border border-ink/10 bg-warmwhite" data-testid="ai-discovery-checklist">
            <p className="border-b border-ink/10 px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Readiness checklist</p>
            <ul>
              {report.checks.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 border-b border-ink/5 px-6 py-3.5 last:border-0" data-testid={`check-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium text-ink">{c.label}</p>
                    {c.detail && <p className="mt-0.5 text-xs text-ink/50">{c.detail}</p>}
                  </div>
                  <Badge status={c.status} />
                </li>
              ))}
            </ul>
          </div>

          {report.broken_links?.length > 0 && (
            <div className="mt-6 border border-burgundy/30 bg-burgundy/5 p-6" data-testid="broken-links">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-burgundy">Broken URLs</p>
              <ul className="mt-3 space-y-1.5">
                {report.broken_links.map((b) => (
                  <li key={b.url} className="break-all text-sm text-ink"><span className="font-semibold text-burgundy">[{b.status}]</span> {b.url}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-6 text-xs text-ink/40">Generated {new Date(report.generated_at).toLocaleString()}. This report is admin-only and never exposed publicly.</p>
        </>
      )}
    </div>
  );
}
